/**
 * Time-of-use (TOU) tariff schedule helpers for the charge-point detail screen.
 *
 * The `/tariffs/resolve` endpoint returns ALL tariffs applicable to a connector, each
 * with an Energy price component and an optional time-window restriction. These helpers
 * flatten that response into a display schedule and determine which window is active for
 * the current device time — mirroring the backend `getApplicableTariff` matching so the
 * highlighted rate matches what csms-sync will bill.
 */

export interface TariffScheduleEntry {
  price: number;
  vat?: number;
  /** HH:MM, inclusive start of the window. Absent ⇒ always-on (flat) rate. */
  startTime?: string;
  /** HH:MM, exclusive end of the window. Absent ⇒ always-on (flat) rate. */
  endTime?: string;
  /** 0-6 (Sun-Sat). Absent/empty ⇒ every day. */
  dayOfWeek?: number[];
}

// Shape of a resolved tariff as returned by GET /tariffs/resolve (subset we consume).
interface ResolvedPriceComponent {
  type: string;
  price: number;
  vat?: number;
}
interface ResolvedRestriction {
  startTime?: string;
  endTime?: string;
  dayOfWeek?: number[];
}
interface ResolvedTariffElement {
  priceComponents?: ResolvedPriceComponent[];
  restrictions?: ResolvedRestriction[];
}
interface ResolvedTariff {
  tariffElements?: ResolvedTariffElement[];
}

/**
 * Flatten resolved tariffs into a schedule of Energy-rate windows. One entry per tariff
 * element that carries an Energy price component. Elements without an Energy component are
 * skipped (this screen has only ever shown the Energy rate).
 */
export function mapTariffsToSchedule(
  tariffs: ResolvedTariff[] | null | undefined,
): TariffScheduleEntry[] {
  if (!Array.isArray(tariffs)) return [];

  const entries: TariffScheduleEntry[] = [];

  for (const tariff of tariffs) {
    if (!tariff?.tariffElements) continue;

    for (const element of tariff.tariffElements) {
      const energy = element.priceComponents?.find((pc) => pc.type === 'Energy');
      if (!energy) continue;

      // A single element may in principle carry multiple restrictions; the first one with
      // a time window defines the display window (backend matches on any restriction).
      const restriction = element.restrictions?.find(
        (r) => r.startTime || r.endTime || (r.dayOfWeek && r.dayOfWeek.length > 0),
      );

      entries.push({
        price: energy.price,
        vat: energy.vat,
        startTime: restriction?.startTime,
        endTime: restriction?.endTime,
        dayOfWeek: restriction?.dayOfWeek,
      });
    }
  }

  return entries;
}

/** HH:MM for a Date in local time. */
function toHHMM(date: Date): string {
  const h = String(date.getHours()).padStart(2, '0');
  const m = String(date.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}

/**
 * Is this schedule entry active at `now` (device local time)?
 *
 * Mirrors the backend matching: no window ⇒ always active; time window supports
 * midnight-crossing (start > end ⇒ active if t >= start OR t < end); a dayOfWeek list, if
 * present, must include the current day.
 */
export function isWindowActiveNow(entry: TariffScheduleEntry, now: Date = new Date()): boolean {
  if (entry.dayOfWeek && entry.dayOfWeek.length > 0 && !entry.dayOfWeek.includes(now.getDay())) {
    return false;
  }

  // No time bounds ⇒ flat rate, always active.
  if (!entry.startTime || !entry.endTime) return true;

  const t = toHHMM(now);
  const { startTime: start, endTime: end } = entry;

  // End is inclusive to the minute, because that is how the rates are written:
  // prod runs 09:00–15:59 and 16:00–08:59, where 15:59 means "through 15:59:59".
  // Treating it as exclusive left 15:59 and 08:59 matching no window at all, so
  // for those two minutes a day no rate showed as current anywhere.
  // Windows written to abut on the same minute (…–16:00 and 16:00–…) would now
  // both match at the boundary; the first is used, and no live tariff does this.
  if (start <= end) {
    // Normal same-day range, e.g. 09:00–15:59.
    return t >= start && t <= end;
  }
  // Crosses midnight, e.g. 16:00–08:59.
  return t >= start || t <= end;
}

/** Format a window for display, e.g. "09:00–15:59". Empty for a flat (always-on) rate. */
export function formatWindow(entry: TariffScheduleEntry): string {
  if (!entry.startTime || !entry.endTime) return '';
  return `${entry.startTime}–${entry.endTime}`;
}
